import { Button } from '@components/ui/button';
import { ReasoningTooltip, ReviewButton } from '@components/ui/review-button';
import { Check, EyeOff, Sparkles } from 'lucide-react';
import React from 'react';

import type { FieldEvidence } from '@/components/fields/types';
import type { FormField } from '@/types/formSchema';
import { formatSuggestionValue } from '@/utils/suggestionFormatters';

interface SuggestionChipProps {
  /**
   * The raw suggestion value from extracted_data
   */
  suggestion: any;

  /**
   * The field schema for formatting
   */
  field: FormField;

  /**
   * Callback when user accepts the suggestion
   */
  onAccept: () => void;

  /**
   * Callback when user rejects the suggestion
   */
  onReject: () => void;

  /**
   * Whether to show the hide button (only for textarea)
   */
  showHideButton?: boolean;

  /**
   * Optional per-field extraction evidence. When present (with onNavigateToCall),
   * the chip shows the Review button + reasoning lightbulb alongside the value.
   */
  evidence?: FieldEvidence[];
  sourceCallId?: string;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;

  /**
   * Optional className for custom styling
   */
  className?: string;
}

/**
 * SuggestionChip - A green chip component displaying AI suggestions
 * Shows formatted suggestion value with accept (✓) and hide (👁) buttons.
 *
 * Features:
 * - Formats suggestion based on field type (date, select, multi-select, etc.)
 * - Displays full text without truncation (takes full width)
 * - Keyboard accessible
 * - Follows shadcn/ui theme conventions
 *
 * @component
 * @example
 * ```tsx
 * <SuggestionChip
 *   suggestion="John Doe"
 *   field={field}
 *   onAccept={() => onChange("John Doe")}
 *   onReject={() => {}}
 * />
 * ```
 */
export const SuggestionChip: React.FC<SuggestionChipProps> = ({
  suggestion,
  field,
  onAccept,
  onReject,
  showHideButton = false,
  evidence,
  sourceCallId,
  onNavigateToCall,
  onNavigateToAudit,
  className = '',
}) => {
  // Format the suggestion value for display
  const formattedValue = formatSuggestionValue(suggestion, field);

  return (
    <div
      className={`flex items-start gap-2 rounded-md border border-ai-suggestion-border bg-ai-suggestion px-3 py-1.5 w-full ${className}`}
      role="region"
      aria-label={`AI suggestion for ${field.label}`}
    >
      {/* AI icon */}
      <Sparkles className="h-4 w-4 text-ai-suggestion-icon shrink-0 mt-0.5" aria-hidden="true" />

      {/* Suggestion text */}
      <span
        id={`suggestion-${field.id}`}
        className="text-sm text-ai-suggestion-foreground flex-1 text-left"
        aria-label={`Suggested value: ${formattedValue}`}
      >
        {formattedValue}
      </span>

      {/* Review button + reasoning lightbulb (extraction provenance) */}
      {onNavigateToCall && !!evidence?.length && (
        <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
          <ReviewButton
            sourceCallId={sourceCallId}
            evidence={evidence}
            onNavigateToCall={onNavigateToCall}
            onNavigateToAudit={onNavigateToAudit}
            className="text-xs"
          />
          <ReasoningTooltip evidence={evidence} />
        </div>
      )}

      {/* Hide button - only shown for textarea */}
      {showHideButton && (
        <Button
          variant="ghost"
          size="sm"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onReject();
          }}
          className="h-6 w-6 p-0 text-ai-suggestion-foreground hover:bg-ai-suggestion-border hover:text-ai-suggestion-foreground"
          title="Hide suggestion"
          aria-label="Hide suggestion"
        >
          <EyeOff className="h-4 w-4" />
        </Button>
      )}

      {/* Accept button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onAccept();
        }}
        className="h-6 w-6 p-0 text-ai-suggestion-foreground hover:bg-ai-suggestion-border hover:text-ai-suggestion-foreground"
        title="Accept suggestion"
        aria-label={`Accept suggestion: ${formattedValue}`}
        aria-describedby={`suggestion-${field.id}`}
      >
        <Check className="h-4 w-4" />
      </Button>
    </div>
  );
};

SuggestionChip.displayName = 'SuggestionChip';
